import { collection, getDocs, doc, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../admin/firebase/config';

/**
 * Migration Script: Move global menu items to outlet-specific collections
 *
 * NEW STRUCTURE:
 * - outlets/{outletId}/menu_items/{itemId}
 *
 * Instead of one global menu_items collection with outlet IDs,
 * each outlet has its own menu_items subcollection.
 */

/**
 * Copy all menu items from global collection to specific outlet
 * @param {string} outletId - The outlet ID to copy menu items to
 * @returns {Promise<{success: boolean, count: number}>}
 */
export const copyMenuToOutlet = async (outletId) => {
  try {
    console.log(`📋 Starting menu migration for outlet: ${outletId}`);

    // Get all menu items from global collection
    const menuItemsRef = collection(db, 'menu_items');
    const snapshot = await getDocs(menuItemsRef);

    console.log(`📊 Found ${snapshot.size} menu items to copy`);

    // Use batch writes for efficiency (max 500 per batch)
    const batches = [];
    let currentBatch = writeBatch(db);
    let operationCount = 0;
    let totalCopied = 0;

    snapshot.docs.forEach((menuDoc) => {
      const menuData = menuDoc.data();

      // Create reference to outlet-specific menu item
      const outletMenuItemRef = doc(db, 'outlets', outletId, 'menu_items', menuDoc.id);

      // Add to batch
      currentBatch.set(outletMenuItemRef, {
        ...menuData,
        copiedFrom: 'global',
        copiedAt: new Date(),
        outletId: outletId
      });

      operationCount++;
      totalCopied++;

      // Firestore batch limit is 500 operations
      if (operationCount === 500) {
        batches.push(currentBatch);
        currentBatch = writeBatch(db);
        operationCount = 0;
      }
    });

    // Add remaining operations
    if (operationCount > 0) {
      batches.push(currentBatch);
    }

    // Commit all batches
    console.log(`💾 Committing ${batches.length} batch(es)...`);
    await Promise.all(batches.map(batch => batch.commit()));

    console.log(`✅ Successfully copied ${totalCopied} menu items to outlet ${outletId}`);

    return {
      success: true,
      count: totalCopied,
      message: `Copied ${totalCopied} menu items to outlet`
    };

  } catch (error) {
    console.error('❌ Error copying menu to outlet:', error);
    return {
      success: false,
      count: 0,
      error: error.message
    };
  }
};

/**
 * Copy menu items to ALL active outlets
 * @returns {Promise<{success: boolean, results: Array}>}
 */
export const copyMenuToAllOutlets = async () => {
  try {
    console.log('📋 Starting menu migration for ALL outlets...');

    // Get all active outlets
    const outletsRef = collection(db, 'outlets');
    const outletsSnapshot = await getDocs(outletsRef);

    const activeOutlets = outletsSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(outlet => outlet.isActive);

    console.log(`🏢 Found ${activeOutlets.length} active outlets`);

    // Copy menu to each outlet
    const results = [];
    for (const outlet of activeOutlets) {
      console.log(`\n📍 Processing outlet: ${outlet.name}`);
      const result = await copyMenuToOutlet(outlet.id);
      results.push({
        outletId: outlet.id,
        outletName: outlet.name,
        ...result
      });
    }

    const totalCopied = results.reduce((sum, r) => sum + r.count, 0);
    console.log(`\n✅ Migration complete! Total items copied: ${totalCopied}`);

    return {
      success: true,
      results,
      totalCopied,
      outletsProcessed: activeOutlets.length
    };

  } catch (error) {
    console.error('❌ Error in bulk migration:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete outlet-specific menu items (rollback migration)
 * @param {string} outletId - The outlet ID to clear menu from
 * @returns {Promise<{success: boolean, count: number}>}
 */
export const clearOutletMenu = async (outletId) => {
  try {
    console.log(`🗑️ Clearing menu for outlet: ${outletId}`);

    const outletMenuRef = collection(db, 'outlets', outletId, 'menu_items');
    const snapshot = await getDocs(outletMenuRef);

    const batches = [];
    let currentBatch = writeBatch(db);
    let operationCount = 0;
    let totalDeleted = 0;

    snapshot.docs.forEach((doc) => {
      currentBatch.delete(doc.ref);
      operationCount++;
      totalDeleted++;

      if (operationCount === 500) {
        batches.push(currentBatch);
        currentBatch = writeBatch(db);
        operationCount = 0;
      }
    });

    if (operationCount > 0) {
      batches.push(currentBatch);
    }

    await Promise.all(batches.map(batch => batch.commit()));

    console.log(`✅ Cleared ${totalDeleted} menu items from outlet ${outletId}`);

    return {
      success: true,
      count: totalDeleted
    };

  } catch (error) {
    console.error('❌ Error clearing outlet menu:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get menu statistics for an outlet
 * @param {string} outletId - The outlet ID
 * @returns {Promise<object>}
 */
export const getOutletMenuStats = async (outletId) => {
  try {
    const outletMenuRef = collection(db, 'outlets', outletId, 'menu_items');
    const snapshot = await getDocs(outletMenuRef);

    const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const stats = {
      total: items.length,
      available: items.filter(i => i.isAvailable).length,
      unavailable: items.filter(i => !i.isAvailable).length,
      veg: items.filter(i => i.isVeg).length,
      nonVeg: items.filter(i => !i.isVeg).length,
      categories: [...new Set(items.map(i => i.category))],
      hasMenu: items.length > 0
    };

    return stats;

  } catch (error) {
    console.error('❌ Error getting menu stats:', error);
    return {
      total: 0,
      hasMenu: false,
      error: error.message
    };
  }
};

export default {
  copyMenuToOutlet,
  copyMenuToAllOutlets,
  clearOutletMenu,
  getOutletMenuStats
};
